import { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowDown2, ArrowUp2 } from "iconsax-reactjs";
import AdminLayoutSideBarItemDropdown from "./AdminLayoutSideBarItemDropdown";

interface SideBarItemProps {
  item: {
    id: string;
    label: string;
    icon?: React.ReactNode;
    path?: string;
    notifition?: string;
    children?: {
      id: string;
      label: string;
      path?: string;
      notifition?: string;
    }[];
  };
  notifications: string[];
  enabledItemIds: Set<string>;
  isRestrictedRole?: boolean;
}

export default function AdminLayoutSideBarItem({
  item,
  notifications,
  enabledItemIds,
  isRestrictedRole = false,
}: SideBarItemProps) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  const itemNotificationKeys = useMemo(
    () =>
      item.notifition
        ?.trim()
        .split(/\s+/)
        .filter((key) => key.length > 0) || [],
    [item.notifition],
  );

  const hasItemNotification = itemNotificationKeys.some((key) =>
    notifications.includes(key),
  );

  const hasChildNotification =
    hasChildren &&
    (item.children || []).some((child) => {
      if (!child.notifition) return false;
      const childNotifKey = child.notifition.trim();
      return childNotifKey.length > 0 && notifications.includes(childNotifKey);
    });

  const showNotif = hasItemNotification || hasChildNotification;
  const isActive = location.pathname === item.path;
  const restrictedRoleAllowedItems = [
    "logout",
    "sepehr-jo",
    "sepehr-jo-authorization",
    "questions-list",
    "answers-list",
    "authorization-student",
    "authorization-teacher",
  ];

  const restrictedRoleAllowedChildren = useMemo(
    () =>
      item.children
        ?.filter((child) => restrictedRoleAllowedItems.includes(child.id))
        .map((child) => child.id) || [],
    [item.children],
  );
  const isDisabled =
    !enabledItemIds.has(item.id) ||
    (isRestrictedRole && !restrictedRoleAllowedItems.includes(item.id));

  const clickHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isDisabled) return;

    if (hasChildren) {
      setIsOpen((prev) => !prev);
    } else if (item.id === "logout") {
      localStorage.clear();
      navigate("/");
    } else if (item.path) {
      navigate(item.path);
    }
  };

  return (
    <div className="flex w-full flex-col overflow-hidden">
      <button
        onClick={clickHandler}
        title={isDisabled ? "شما دسترسی ندارید" : ""}
        className={`flex w-full items-center rounded-xl px-4 py-2.5 relative transition-colors duration-200 ${
          isDisabled
            ? "cursor-not-allowed opacity-60"
            : "hover:bg-primaryVar-50"
        }`}
      >
        <div className="flex w-full items-center justify-between gap-2">
          <div className="flex w-full items-center gap-2 relative">
            <span
              className={`${isActive ? "text-primaryVar" : "text-gray-500"}`}
            >
              {item.icon}
            </span>

            <span
              className={`text-right text-sm ${
                isActive ? "text-primaryVar" : "text-gray-500"
              }`}
            >
              {item.label}
            </span>
            {showNotif && (
              <span className="h-2 w-2 rounded-full bg-errorVar absolute right-40 top-1/2 -translate-y-1/2"></span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {hasChildren &&
              (isOpen ? (
                <span
                  className={`${isActive ? "text-primaryVar" : "text-gray-500"}`}
                >
                  <ArrowUp2 size={20} />
                </span>
              ) : (
                <span
                  className={`${isActive ? "text-primaryVar" : "text-gray-500"}`}
                >
                  <ArrowDown2 size={20} />
                </span>
              ))}
          </div>
        </div>
      </button>
      {hasChildren && (
        <AdminLayoutSideBarItemDropdown
          isOpen={isOpen}
          data={item.children || []}
          notifications={notifications}
          parentNotificationKeys={itemNotificationKeys}
          isParentDisabled={isDisabled}
          isRestrictedRole={isRestrictedRole}
          restrictedRoleAllowedChildren={restrictedRoleAllowedChildren}
        />
      )}
    </div>
  );
}
